/**
 * Benevoles — gestion des équipes et du planning des bénévoles (3 onglets).
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { colors, fonts, goldGradient } from "../styles/tokens";
import { benevolesApi, evenementsApi } from "../services/api";
import useMembers from "../hooks/useMembers";
import useToast from "../hooks/useToast";
import Card from "../components/Card";
import Badge from "../components/Badge";
import Button from "../components/Button";
import Avatar from "../components/Avatar";
import EquipeForm from "../components/EquipeForm";
import AssignBenevolesModal from "../components/AssignBenevolesModal";

const TABS = [
  { id: "planning",  label: "Planning",     icon: "📅" },
  { id: "equipes",   label: "Équipes",      icon: "👥" },
  { id: "stats",     label: "Statistiques", icon: "📊" },
];

const STATUT_CFG = {
  confirme:   { icon: "✓",  color: "#2DD4A0", label: "Confirmé" },
  en_attente: { icon: "⏳", color: "#FB923C", label: "En attente" },
  refuse:     { icon: "✗",  color: "#E05B6A", label: "Refusé" },
  remplace:   { icon: "↻",  color: "#8A97B0", label: "Remplacé" },
};

export default function Benevoles() {
  const [active, setActive] = useState("planning");

  return (
    <div style={{ fontFamily: fonts.body, color: colors.text }}>
      <header style={{ marginBottom: "24px" }}>
        <h1 style={{ fontFamily: fonts.heading, fontSize: "36px", fontWeight: 700, marginBottom: "4px" }}>
          Bénévoles
        </h1>
        <p style={{ color: colors.textMuted, fontSize: "14px" }}>
          Équipes de service et planning des cultes
        </p>
      </header>

      <div style={{
        display: "flex", gap: "4px", flexWrap: "wrap",
        background: colors.card, padding: "6px", borderRadius: "12px",
        border: `1px solid ${colors.border}`,
        marginBottom: "20px",
      }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setActive(t.id)}
            style={{
              flex: "1 1 auto",
              minWidth: 130,
              padding: "10px 16px",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: active === t.id ? 600 : 500,
              color: active === t.id ? colors.bg : colors.text,
              background: active === t.id ? goldGradient : "transparent",
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "8px",
            }}
          >
            <span>{t.icon}</span><span>{t.label}</span>
          </button>
        ))}
      </div>

      {active === "planning" && <PlanningTab />}
      {active === "equipes"  && <EquipesTab />}
      {active === "stats"    && <StatsTab />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// ONGLET PLANNING
// ════════════════════════════════════════════════════════════

function PlanningTab() {
  const toast = useToast();
  const [plannings, setPlannings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [pickedEvent, setPickedEvent] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [plRes, evRes] = await Promise.all([
        benevolesApi.listPlanning(),
        evenementsApi.list({ type: "culte" }),
      ]);
      setPlannings(plRes.data || []);
      setEvents(evRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  // Groupe les assignations par évènement (ou par date si pas d'évènement)
  const groups = useMemo(() => {
    const map = {};
    plannings.forEach((p) => {
      const key = p.evenement_id ? `evt-${p.evenement_id}` : `date-${p.date_service}`;
      if (!map[key]) {
        map[key] = {
          key,
          evenement_id: p.evenement_id,
          titre: p.evenement_titre || `Service du ${new Date(p.date_service).toLocaleDateString("fr-FR")}`,
          date: p.date_service,
          items: [],
        };
      }
      map[key].items.push(p);
    });
    return Object.values(map).sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  }, [plannings]);

  const handleRepondre = async (planning, statut) => {
    try {
      await benevolesApi.repondre(planning.id, { statut });
      toast.success(statut === "confirme" ? "Confirmé ✓" : "Refusé ✗");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleRemplacer = async (planning) => {
    if (!window.confirm(`Marquer « ${planning.membre_nom} » comme à remplacer ?`)) return;
    try {
      await benevolesApi.repondre(planning.id, { statut: "remplace" });
      toast.success("Marqué comme à remplacer");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  if (loading) return <div style={{ color: colors.textMuted }}>Chargement...</div>;

  return (
    <>
      {/* Sélecteur événement pour assigner */}
      {events.length > 0 && (
        <Card style={{ marginBottom: "16px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
            <div>
              <div style={{ fontFamily: fonts.heading, fontSize: "18px", fontWeight: 600 }}>
                Assigner pour un culte
              </div>
              <div style={{ color: colors.textMuted, fontSize: "13px" }}>
                Cliquez « Gérer » sur un culte pour y ajouter des bénévoles.
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "12px" }}>
            {events.slice(0, 6).map((e) => (
              <Button
                key={e.id}
                size="sm"
                variant="ghost"
                onClick={() => { setPickedEvent(e); setAssignOpen(true); }}
              >
                ⛪ {new Date(e.date_debut).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })} — {e.titre}
              </Button>
            ))}
          </div>
        </Card>
      )}

      {/* Liste groupée */}
      {groups.length === 0 ? (
        <Card style={{ textAlign: "center", padding: "60px" }}>
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>📅</div>
          <h3 style={{ fontFamily: fonts.heading, fontSize: "22px" }}>Aucune assignation</h3>
          <p style={{ color: colors.textMuted, marginTop: "8px" }}>
            Créez d'abord une équipe puis assignez des bénévoles à un culte.
          </p>
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {groups.map((g) => {
            const counts = g.items.reduce((acc, p) => {
              acc[p.statut] = (acc[p.statut] || 0) + 1;
              return acc;
            }, {});
            return (
              <Card key={g.key}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px", marginBottom: "12px" }}>
                  <div>
                    <h3 style={{ fontFamily: fonts.heading, fontSize: "20px", fontWeight: 600 }}>
                      ⛪ {g.titre}
                    </h3>
                    <div style={{ color: colors.textMuted, fontSize: "13px", marginTop: "2px" }}>
                      {new Date(g.date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                      {" · "}
                      <span style={{ color: STATUT_CFG.confirme.color }}>{counts.confirme || 0} confirmés</span>
                      {" · "}
                      <span style={{ color: STATUT_CFG.en_attente.color }}>{counts.en_attente || 0} en attente</span>
                      {(counts.refuse || 0) > 0 && (
                        <>{" · "}<span style={{ color: STATUT_CFG.refuse.color }}>{counts.refuse} refusés</span></>
                      )}
                    </div>
                  </div>
                  {g.evenement_id && (
                    <Button size="sm" variant="ghost" onClick={() => {
                      const ev = events.find((e) => e.id === g.evenement_id);
                      if (ev) { setPickedEvent(ev); setAssignOpen(true); }
                    }}>
                      Gérer
                    </Button>
                  )}
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {g.items.map((p) => {
                    const cfg = STATUT_CFG[p.statut] || STATUT_CFG.en_attente;
                    return (
                      <div key={p.id} style={{
                        display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap",
                        padding: "10px 12px",
                        background: colors.bg,
                        borderRadius: "10px",
                        borderLeft: `3px solid ${p.equipe_couleur || colors.gold}`,
                      }}>
                        <Avatar name={p.membre_nom} size="sm" />
                        <div style={{ flex: 1, minWidth: 180 }}>
                          <div style={{ fontWeight: 500 }}>{p.membre_nom}</div>
                          <div style={{ fontSize: "12px", color: colors.textMuted }}>
                            <span style={{ color: p.equipe_couleur }}>● {p.equipe_nom}</span>
                            {p.role && <> · {p.role}</>}
                          </div>
                        </div>
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: "6px",
                          padding: "4px 10px", borderRadius: "999px",
                          background: `${cfg.color}20`, color: cfg.color,
                          fontSize: "12px", fontWeight: 600,
                        }}>
                          <span>{cfg.icon}</span><span>{cfg.label}</span>
                        </span>
                        <div style={{ display: "flex", gap: "4px" }}>
                          {p.statut === "en_attente" && (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => handleRepondre(p, "confirme")}>✓ Confirmer</Button>
                              <Button size="sm" variant="ghost" onClick={() => handleRepondre(p, "refuse")}>✗ Refuser</Button>
                            </>
                          )}
                          {p.statut === "refuse" && (
                            <Button size="sm" variant="danger" onClick={() => handleRemplacer(p)}>Remplacer</Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <AssignBenevolesModal
        open={assignOpen}
        onClose={() => { setAssignOpen(false); setPickedEvent(null); }}
        evenement={pickedEvent}
        onChange={load}
      />
    </>
  );
}

// ════════════════════════════════════════════════════════════
// ONGLET ÉQUIPES
// ════════════════════════════════════════════════════════════

function EquipesTab() {
  const { eglises } = useMembers();
  const toast = useToast();
  const [equipes, setEquipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await benevolesApi.listEquipes();
      setEquipes(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (payload) => {
    try {
      if (editing) {
        await benevolesApi.updateEquipe(editing.id, payload);
        toast.success("Équipe mise à jour");
      } else {
        await benevolesApi.createEquipe(payload);
        toast.success(`Équipe « ${payload.nom} » créée`);
      }
      setFormOpen(false);
      setEditing(null);
      await load();
      return { ok: true };
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur";
      toast.error(msg);
      return { ok: false, error: msg };
    }
  };

  const handleDelete = async (eq) => {
    try {
      await benevolesApi.removeEquipe(eq.id);
      toast.success("Équipe désactivée");
      setFormOpen(false);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  if (loading) return <div style={{ color: colors.textMuted }}>Chargement...</div>;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "12px" }}>
        <div style={{ color: colors.textMuted, fontSize: "14px" }}>
          {equipes.length} équipe{equipes.length > 1 ? "s" : ""} active{equipes.length > 1 ? "s" : ""}
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }}>+ Nouvelle équipe</Button>
      </div>

      {equipes.length === 0 ? (
        <Card style={{ textAlign: "center", padding: "60px" }}>
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>👥</div>
          <h3 style={{ fontFamily: fonts.heading, fontSize: "22px" }}>Aucune équipe</h3>
          <p style={{ color: colors.textMuted, marginTop: "8px" }}>
            Créez une équipe (Louange, Son, Accueil…) pour commencer à organiser vos services.
          </p>
        </Card>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "16px" }}>
          {equipes.map((e) => (
            <Card key={e.id} hover>
              <div style={{ borderLeft: `4px solid ${e.couleur}`, paddingLeft: "12px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                  <h3 style={{ fontFamily: fonts.heading, fontSize: "20px", fontWeight: 600 }}>
                    {e.nom}
                  </h3>
                  <Badge variant="default" size="sm">{e.nb_assignations}</Badge>
                </div>
                <div style={{ color: colors.textMuted, fontSize: "13px", marginBottom: "8px" }}>
                  ⛪ {e.eglise_nom}
                </div>
                <div style={{ fontSize: "13px", marginBottom: "10px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                  {e.responsable_nom ? (
                    <>
                      <span>👤 Responsable : <strong>{e.responsable_nom}</strong></span>
                      <button
                        onClick={() => { setEditing(e); setFormOpen(true); }}
                        style={{
                          color: colors.textMuted, fontSize: "11px",
                          background: "transparent", padding: "2px 8px",
                          borderRadius: "999px", border: `1px solid ${colors.border}`,
                          transition: "all 150ms",
                        }}
                        onMouseEnter={(ev) => { ev.currentTarget.style.color = colors.gold; ev.currentTarget.style.borderColor = colors.goldBorder; }}
                        onMouseLeave={(ev) => { ev.currentTarget.style.color = colors.textMuted; ev.currentTarget.style.borderColor = colors.border; }}
                      >
                        Changer
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => { setEditing(e); setFormOpen(true); }}
                      style={{
                        color: colors.gold, fontSize: "13px", fontWeight: 500,
                        background: "transparent", padding: "4px 0",
                        textDecoration: "underline",
                        textUnderlineOffset: "3px",
                      }}
                    >
                      + Assigner un responsable
                    </button>
                  )}
                </div>

                {/* Taux confirmation */}
                <div style={{ marginBottom: "10px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", marginBottom: "4px" }}>
                    <span style={{ color: colors.textMuted }}>Taux de confirmation</span>
                    <span style={{ color: colors.text, fontWeight: 600 }}>{e.taux_confirmation}%</span>
                  </div>
                  <div style={{ height: 6, background: colors.border, borderRadius: 999, overflow: "hidden" }}>
                    <div style={{
                      width: `${e.taux_confirmation}%`,
                      height: "100%",
                      background: e.couleur,
                      transition: "width 400ms",
                    }} />
                  </div>
                </div>

                <Button size="sm" variant="ghost" onClick={() => { setEditing(e); setFormOpen(true); }}>
                  Modifier
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <EquipeForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        eglises={eglises}
        initial={editing}
      />
    </>
  );
}

// ════════════════════════════════════════════════════════════
// ONGLET STATS
// ════════════════════════════════════════════════════════════

function StatsTab() {
  const toast = useToast();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    benevolesApi.stats()
      .then(({ data }) => setStats(data))
      .catch((err) => toast.error(err.response?.data?.detail || "Erreur"))
      .finally(() => setLoading(false));
  }, [toast]);

  if (loading) return <div style={{ color: colors.textMuted }}>Chargement...</div>;
  if (!stats) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* KPIs globaux */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
        <Kpi label="Taux de confirmation" value={`${stats.taux_confirmation}%`} accent={colors.gold} />
        <Kpi label="Total assignations"   value={stats.total} />
        <Kpi label="Confirmées"            value={stats.confirmes} accent={STATUT_CFG.confirme.color} />
        <Kpi label="En attente"            value={stats.en_attente} accent={STATUT_CFG.en_attente.color} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: "16px" }}>
        {/* Top bénévoles */}
        <Card>
          <h3 style={{ fontFamily: fonts.heading, fontSize: "20px", marginBottom: "12px" }}>
            🏆 Bénévoles les plus fiables
          </h3>
          {stats.top_benevoles.length === 0 ? (
            <p style={{ color: colors.textMuted, fontSize: "13px" }}>
              Pas encore assez de données (min. 2 assignations).
            </p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {stats.top_benevoles.map((b, i) => (
                <div key={b.membre_id} style={{
                  display: "flex", alignItems: "center", gap: "10px",
                  padding: "8px 12px",
                  background: colors.bg,
                  borderRadius: "10px",
                }}>
                  <span style={{
                    width: 24, height: 24, borderRadius: "50%",
                    background: i < 3 ? colors.gold : colors.border,
                    color: i < 3 ? colors.bg : colors.textMuted,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    fontSize: "12px", fontWeight: 700,
                  }}>{i + 1}</span>
                  <Avatar name={b.full_name} size="xs" />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "14px", fontWeight: 500 }}>{b.full_name}</div>
                    <div style={{ fontSize: "11px", color: colors.textMuted }}>
                      {b.nb_confirmes}/{b.nb_assignations} confirmés
                    </div>
                  </div>
                  <span style={{ fontFamily: fonts.heading, fontWeight: 700, color: colors.gold }}>
                    {b.taux_confirmation}%
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Postes non pourvus */}
        <Card>
          <h3 style={{ fontFamily: fonts.heading, fontSize: "20px", marginBottom: "12px" }}>
            ⚠ Cultes sans bénévole
          </h3>
          {stats.postes_non_pourvus.length === 0 ? (
            <p style={{ color: colors.green, fontSize: "13px" }}>
              ✓ Tous les cultes à venir ont au moins une assignation.
            </p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {stats.postes_non_pourvus.map((p) => (
                <div key={p.evenement_id} style={{
                  padding: "8px 12px",
                  background: "rgba(224,91,106,0.08)",
                  borderRadius: "10px",
                  borderLeft: `3px solid ${colors.rose}`,
                  fontSize: "13px",
                }}>
                  <div style={{ fontWeight: 500 }}>{p.evenement_titre}</div>
                  <div style={{ color: colors.textMuted, fontSize: "12px", marginTop: "2px" }}>
                    {new Date(p.date_debut).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
                    {" · "}{p.eglise_nom}
                    {" · "}{p.nb_equipes_sans_benevole} équipe{p.nb_equipes_sans_benevole > 1 ? "s" : ""} disponible{p.nb_equipes_sans_benevole > 1 ? "s" : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div style={{ marginTop: "12px" }}>
            <Link to="/evenements" style={{ textDecoration: "none" }}>
              <Button size="sm" variant="ghost">Voir les événements →</Button>
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent = colors.text }) {
  return (
    <Card hover>
      <div style={{ fontSize: "13px", color: colors.textMuted, marginBottom: "8px" }}>{label}</div>
      <div style={{ fontFamily: fonts.heading, fontSize: "30px", fontWeight: 700, color: accent }}>
        {value}
      </div>
    </Card>
  );
}
